import { Injectable } from '@angular/core';
import { filterNullOrUndefined } from '@ats/utility';
import { LocalizationService } from '@cm/core/localization';
import { CmEventService } from '@cm/core/system';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CmSystemEvent } from 'libs/core/system/src/models/system-event';
import { BehaviorSubject, Observable, Subject, of } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { NoticeType } from './notice-type.enum';
import { Notice } from './notice.interface';

const enum NotificationActionTypes {
	UPDATE_LOADERS = 'Update loader array',
	UPDATE_NOTICE = 'Update notice',
}

interface NotificationAction {
	readonly type: NotificationActionTypes;
	readonly payload: string[] | Notice | boolean;
}

class NotificationState {
	loaders: string[] = [];
	notice: Notice | null = null;
}

const reduce = (state: NotificationState, action: NotificationAction) => {
	switch (action.type) {
		case NotificationActionTypes.UPDATE_LOADERS:
			return {
				...state,
				loaders: [...(action.payload as string[])],
			};
		case NotificationActionTypes.UPDATE_NOTICE:
			return {
				...state,
				notice: action.payload as Notice,
			};
	}
};

@UntilDestroy()
@Injectable({
	providedIn: 'root',
})
export class AtsNotificationService {
	private state = new NotificationState();
	private dispatch = new BehaviorSubject<NotificationState>(this.state);
	private readonly reloadSubject = new Subject<string | null>();
	private readonly loaderMessageSubj = new Subject<{
		key: string;
		defaultValue: string;
	} | null>();

	loading$: Observable<boolean> = this.dispatch.asObservable().pipe(
		map(state => state.loaders.length > 0),
		distinctUntilChanged()
	);

	notice$: Observable<Notice> = this.dispatch.asObservable().pipe(
		map(state => state.notice),
		filterNullOrUndefined(),
		distinctUntilChanged()
	);

	loaderMessage$: Observable<{ key: string; defaultValue: string } | null> =
		this.loaderMessageSubj.asObservable();

	readonly reload$: Observable<string | null>;

	constructor(
		private readonly systemEvents: CmEventService,
		private readonly locService: LocalizationService
	) {
		this.reload$ = this.reloadSubject.asObservable();

		this.systemEvents.event$
			.pipe(untilDestroyed(this))
			.subscribe(event => this.handleEvent(event));
	}

	load(key: string): void {
		this.dispatchLoader(this.addLoadKey(key), NotificationActionTypes.UPDATE_LOADERS);
	}

	unload(key: string): void {
		this.dispatchLoader(this.removeLoadKey(key), NotificationActionTypes.UPDATE_LOADERS);
	}

	popLoader(text: { key: string; defaultValue: string }) {
		this.loaderMessageSubj.next(text);
	}

	hideLoader() {
		this.loaderMessageSubj.next(null);
	}

	pop(notice: Notice): void {
		const type = NotificationActionTypes.UPDATE_NOTICE;
		const action = { payload: notice, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	popError(notice: Omit<Notice, 'type'>): void {
		const noticeWithType = {
			...notice,
			type: NoticeType.ERROR,
		};
		this.pop(noticeWithType);
	}

	popSuccess(notice: Omit<Notice, 'type'>): void {
		const noticeWithType = {
			...notice,
			type: NoticeType.SUCCESS,
		};
		this.pop(noticeWithType);
	}

	popInfo(notice: Omit<Notice, 'type'>): void {
		const noticeWithType = {
			...notice,
			type: NoticeType.INFO,
		};
		this.pop(noticeWithType);
	}

	private localizeMessage(message: string): Observable<string | null> {
		if (typeof message === 'string') {
			return this.locService.findValue$(message);
		} else return of(null);
	}

	showSuccess(localization: string, additionalMessage?: string): void {
		this.localizeMessage(localization)
			.pipe(take(1))
			.subscribe(message => {
				if (message) {
					this.popSuccess({
						id: 'success',
						message: additionalMessage ? `${message} ${additionalMessage}` : message,
					});
				}
			});
	}

	showFailure(localization: string, additionalMessage?: string): void {
		this.localizeMessage(localization)
			.pipe(take(1))
			.subscribe(message => {
				if (message) {
					this.popError({
						id: 'failure',
						message: additionalMessage ? `${message} ${additionalMessage}` : message,
					});
				}
			});
	}

	showNeutral(localization: string, additionalMessage?: string): void {
		this.localizeMessage(localization)
			.pipe(take(1))
			.subscribe(message => {
				if (message) {
					this.pop({
						id: 'neutral',
						message: additionalMessage ? `${message} ${additionalMessage}` : message,
						type: NoticeType.NEUTRAL,
					});
				}
			});
	}

	requireReload(message: string | null = null): void {
		this.reloadSubject.next(message);
	}

	private dispatchLoader(loaders: string[], type: NotificationActionTypes): void {
		const action = { payload: loaders, type };
		this.dispatch.next((this.state = reduce(this.state, action)));
	}

	private addLoadKey(key: string): string[] {
		return [...this.removeLoadKey(key), key];
	}

	private removeLoadKey(key: string): string[] {
		return this.state.loaders.filter(ky => ky !== key);
	}

	private handleEvent(event: CmSystemEvent): void {
		let locName: string | null = null;
		switch (event.type) {
			case 'API_ERROR':
				locName = event.id;
				break;
			case 'AUTH_ERROR':
				if (event.action !== 'ban-redirect') {
					locName = this.locService.Keys.Errors.CMSystemErrorAuth;
				}
				break;
			case 'SYSTEM_ERROR':
				locName = this.locService.Keys.Errors.CMSystemErrorDefault;
				break;
			case 'LOAD_START':
				this.load(event.id);
				break;
			case 'LOAD_END':
				this.unload(event.id);
				break;
			case 'RELOAD_REQUIRED':
				this.requireReload();
				break;
		}

		// If we have a loc, use it
		if (locName) {
			this.localizeNotice(locName, event.id, NoticeType.ERROR, event.raw?.message);
		} else if (event.raw?.message) {
			this.popError({
				id: event.id,
				message: event.raw.message,
			});
		}
	}

	private localizeNotice(loc: string, id: string, type: NoticeType, msg?: string): void {
		this.locService
			.findValue$(loc)
			.pipe(
				take(1),
				map(message => {
					if (message && message.length > 0) {
						return message;
					} else if (msg && msg.length > 0) {
						return msg;
					} else {
						return 'An error occurred';
					}
				})
			)
			.subscribe(message => {
				this.pop({
					id,
					message,
					type,
				});
			});
	}
}
